import { Link } from "react-router-dom";
import { ArrowUpRight, Facebook, Instagram } from "lucide-react";
import { EMAIL, HOURS, MAPS_URL, PALISIS_URL, PHONE, PHONE_HREF, ADDRESS } from "@/lib/site";

const LINKS = [
  { label: "Home", to: "/" },
  { label: "Tickets & Pricing", to: "/pricing" },
  { label: "Food & Platters", to: "/menu" },
  { label: "Facilities", to: "/facilities" },
  { label: "Events & Groups", to: "/events" },
  { label: "Plan Your Visit", to: "/times" },
  { label: "Directions", to: "/directions" },
  { label: "Health & Safety", to: "/safety" },
  { label: "Gallery", to: "/gallery" },
  { label: "Reviews", to: "/reviews" },
  { label: "Contact", to: "/contact" },
];

export default function Footer() {
  return (
    <footer className="border-t border-white/10 bg-[#0a0a0b] pb-28 pt-16" data-testid="footer">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <img src="/logo.png" alt="Cool Runnings Cape Town logo" className="h-14 w-auto" />
          <p className="mt-4 max-w-xs text-sm font-bold leading-relaxed text-[#faf5ea]/75">
            Africa's first downhill toboggan park — 1.25 km of gravity-powered family fun in
            Tygervalley, Cape Town. Solar powered, loadshedding-free.
          </p>
          <div className="mt-6 flex items-center gap-3">
            <a
              href="https://www.facebook.com/coolrunningscpt"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Follow Cool Runnings on Facebook"
              className="flex h-10 w-10 items-center justify-center rounded-full bg-[#1877f2] text-white transition-all hover:scale-105 hover:bg-[#0d65d9]"
              data-testid="footer-facebook-link"
            >
              <Facebook size={18} />
            </a>
            <a
              href="https://www.instagram.com/coolrunningscpt"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Follow Cool Runnings on Instagram"
              className="flex h-10 w-10 items-center justify-center rounded-full bg-[radial-gradient(circle_at_30%_110%,#fdf497_0%,#fdf497_5%,#fd5949_45%,#d6249f_60%,#285aeb_90%)] text-white transition-all hover:scale-105"
              data-testid="footer-instagram-link"
            >
              <Instagram size={18} />
            </a>
            <a
              href="https://www.tiktok.com/@cool_runnings_cpt"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Follow Cool Runnings on TikTok"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-white/40 bg-black text-white transition-all hover:scale-105 hover:bg-[#010101]"
              data-testid="footer-tiktok-link"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" className="h-[18px] w-[18px]" aria-hidden="true">
                <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.25-.02-.5-.02-.76s.01-.51.02-.77c.17-1.85 1.04-3.6 2.4-4.87 1.35-1.27 3.18-2.02 5.05-2.06.4-.01.8 0 1.2.02v4.11c-.4-.11-.82-.17-1.24-.15-.9.05-1.77.5-2.32 1.21-.55.7-.79 1.63-.68 2.51.11.88.66 1.68 1.42 2.12.61.36 1.36.49 2.06.36.66-.13 1.26-.52 1.65-1.06.28-.38.46-.83.52-1.29.04-.32.03-.65.03-.98V7.51c.79.98 1.83 1.78 3.03 2.22 1.24.44 2.58.5 3.86.28v4.06c-1.19-.04-2.38-.3-3.47-.81-.45-.2-.88-.46-1.29-.74z" />
              </svg>
            </a>
          </div>
        </div>

        <div>
          <p className="text-xs font-extrabold uppercase tracking-widest text-[#f8b818]">Explore</p>
          <ul className="mt-4 space-y-2.5">
            {LINKS.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  className="text-sm font-bold text-[#faf5ea]/75 transition-colors hover:text-[#f8b818]"
                  data-testid={`footer-link-${l.to === "/" ? "home" : l.to.replace("/", "")}`}
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="text-xs font-extrabold uppercase tracking-widest text-[#f8b818]">Visit</p>
          <ul className="mt-4 space-y-2.5 text-sm font-bold text-[#faf5ea]/75">
            <li>
              <a href={MAPS_URL} target="_blank" rel="noopener noreferrer" className="hover:text-[#f8b818]" data-testid="footer-address-link">
                {ADDRESS}
              </a>
            </li>
            <li>
              <a href={PHONE_HREF} className="hover:text-[#f8b818]" data-testid="footer-phone-link">
                {PHONE}
              </a>
            </li>
            <li>
              <a href={`mailto:${EMAIL}`} className="hover:text-[#f8b818]" data-testid="footer-email-link">
                {EMAIL}
              </a>
            </li>
          </ul>
        </div>

        <div>
          <p className="text-xs font-extrabold uppercase tracking-widest text-[#f8b818]">Hours</p>
          <ul className="mt-4 space-y-2.5 text-sm font-bold text-[#faf5ea]/75">
            <li>Mon–Fri: {HOURS.mon_fri}</li>
            <li>Weekends, Public- and School Holidays: {HOURS.sat_sun}</li>
            <li>Closed 25 December and when it rains</li>
          </ul>
          <a href={PALISIS_URL} target="_blank" rel="noopener noreferrer" className="btn-gold mt-6 !px-5 !py-2.5 !text-xs" data-testid="footer-buy-btn">
            Buy Online &amp; Save 10% <ArrowUpRight size={14} />
          </a>
        </div>
      </div>

      <div className="mx-auto mt-12 flex max-w-7xl flex-col items-center gap-3 border-t border-white/10 px-4 pt-6 text-center text-xs text-[#b9b4a6] sm:flex-row sm:flex-wrap sm:justify-between sm:gap-3 sm:text-left sm:px-6">
        <span data-testid="footer-copyright">© Cool Runnings 2026. All Rights Reserved.</span>
        <span data-testid="footer-fares-note">Fares valid until 30 September 2027</span>
        <span className="w-full text-center sm:w-auto" data-testid="footer-credit">
          Designed by{" "}
          <a
            href="https://bit.ly/SuccessfulJournalsServices"
            target="_blank"
            rel="noopener noreferrer"
            className="font-bold text-[#f8b818]/90 underline-offset-4 transition-colors hover:text-[#f8b818] hover:underline"
            data-testid="footer-credit-link"
          >
            Successful Journals
          </a>
        </span>
      </div>
    </footer>
  );
}
